import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const GamingHistory = ({ gamingData }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('30days');

  const filterOptions = [
    { value: 'all', label: 'All Games' },
    { value: 'recent', label: 'Recently Played' },
    { value: 'favorites', label: 'Favorites' },
    { value: 'completed', label: 'Completed' }
  ];

  const timeframeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 3 Months' },
    { value: 'all', label: 'All Time' }
  ];

  const recentGames = [
    {
      id: 1,
      title: "Cyber Nexus",
      image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&h=300&fit=crop",
      lastPlayed: "2 hours ago",
      playtime: "45.2 hours",
      progress: 78,
      achievements: 12
    },
    {
      id: 2,
      title: "Quantum Realms",
      image: "https://images.pexels.com/photos/442576/pexels-photo-442576.jpeg?w=400&h=300&fit=crop",
      lastPlayed: "1 day ago",
      playtime: "23.8 hours",
      progress: 45,
      achievements: 8
    },
    {
      id: 3,
      title: "Stellar Odyssey",
      image: "/assets/images/no_image.png",
      lastPlayed: "3 days ago",
      playtime: "67.1 hours",
      progress: 92,
      achievements: 24
    },
    {
      id: 4,
      title: "Neon Warriors",
      image: "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=400&h=300&fit=crop",
      lastPlayed: "1 week ago",
      playtime: "12.5 hours",
      progress: 30,
      achievements: 5
    }
  ];

  const achievements = [
    { id: 1, title: "First Victory", description: "Win your first match", icon: "Trophy", earned: true },
    { id: 2, title: "Speed Runner", description: "Complete a level in under 2 minutes", icon: "Zap", earned: true },
    { id: 3, title: "Collector", description: "Find all hidden items", icon: "Star", earned: true },
    { id: 4, title: "Master Player", description: "Reach level 50", icon: "Crown", earned: false },
    { id: 5, title: "Team Player", description: "Play 10 multiplayer matches", icon: "Users", earned: false }
  ];

  const formatPlaytime = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours}h`;
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="GamepadIcon" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Gaming History</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Select
            options={filterOptions}
            value={selectedFilter}
            onChange={setSelectedFilter}
            className="w-32"
          />
          <Select
            options={timeframeOptions}
            value={selectedTimeframe}
            onChange={setSelectedTimeframe}
            className="w-36"
          />
        </div>
      </div>

      <div className="p-6">
        {/* Gaming Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-muted/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-foreground">{gamingData.totalGames}</div>
            <div className="text-sm text-muted-foreground">Games Played</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-foreground">{gamingData.totalPlaytime}h</div>
            <div className="text-sm text-muted-foreground">Total Playtime</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-foreground">{gamingData.achievements}</div>
            <div className="text-sm text-muted-foreground">Achievements</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-foreground">{gamingData.avgSession}h</div>
            <div className="text-sm text-muted-foreground">Avg Session</div>
          </div>
        </div>

        {/* Recently Played Games */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">Recently Played</h3>
          <div className="space-y-4">
            {recentGames.map((game) => (
              <div key={game.id} className="flex items-center space-x-4 p-4 bg-muted/20 rounded-lg hover:bg-muted/30 transition-colors">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <Image
                    src={game.image}
                    alt={game.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate">{game.title}</h4>
                  <p className="text-sm text-muted-foreground">Last played {game.lastPlayed}</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{game.playtime}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Trophy" size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{game.achievements} achievements</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-2">
                  <div className="text-sm font-medium text-foreground">{game.progress}%</div>
                  <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${game.progress}%` }}
                    />
                  </div>
                </div>

                <Button variant="ghost" size="sm" iconName="Play">
                  Play
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Recent Achievements</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {achievements.map((achievement) => (
              <div key={achievement.id} className={`flex items-center space-x-3 p-4 rounded-lg border ${
                achievement.earned 
                  ? 'bg-accent/10 border-accent/20 text-accent' :'bg-muted/20 border-border text-muted-foreground'
              }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  achievement.earned ? 'bg-accent/20' : 'bg-muted/30'
                }`}>
                  <Icon name={achievement.icon} size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium">{achievement.title}</h4>
                  <p className="text-sm opacity-80">{achievement.description}</p>
                </div>
                {achievement.earned && (
                  <Icon name="Check" size={16} className="text-accent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamingHistory;
